/** WorkerPool 当前容量与占用情况的只读诊断快照。 */
export interface WorkerPoolDiagnostics {
  urlCount: number
  created: number
  idle: number
  leasedOrCreating: number
  waiters: number
  maxWorkersPerUrl: number
}

/** Worker 池，管理 Worker 的创建、复用和销毁 */
export class WorkerPool {
  /** 按 URL 分组的空闲 Worker 数组 */
  workers: {
    [url: string]: Worker[]
  }
  /** 每个 URL 已创建的 Worker 总数 */
  private createdCount: { [url: string]: number } = {}
  /** 等待 Worker 归还的回调队列 */
  private waitQueue: { [url: string]: Array<(worker: Worker) => void> } = {}
  /** 串行化 Worker 创建的锁链，确保 wasm 等资源可被浏览器缓存复用 */
  private createChain: { [url: string]: Promise<void> } = {}
  /** 每个 URL 的最大 Worker 数量 */
  private readonly maxWorkers: number

  constructor(maxWorkers = 4) {
    this.workers = {}
    this.maxWorkers = maxWorkers
  }

  /** 获取 Worker 数量快照；不会创建、终止或持有额外 Worker。 */
  getDiagnostics(): WorkerPoolDiagnostics {
    const urls = new Set([
      ...Object.keys(this.workers),
      ...Object.keys(this.createdCount),
      ...Object.keys(this.waitQueue),
    ])
    let created = 0
    let idle = 0
    let waiters = 0
    for (const url of urls) {
      created += this.createdCount[url] ?? 0
      idle += this.workers[url]?.length ?? 0
      waiters += this.waitQueue[url]?.length ?? 0
    }
    return {
      urlCount: urls.size,
      created,
      idle,
      leasedOrCreating: Math.max(0, created - idle),
      waiters,
      maxWorkersPerUrl: this.maxWorkers,
    }
  }

  /**
   * 获取一个可用的 Worker。
   *
   * - 池中有空闲 Worker 时直接返回
   * - 未达上限时串行创建新 Worker（等前一个 ready 后再创建下一个，确保 wasm 进入浏览器缓存）
   * - 已达上限时等待其他调用者归还
   *
   * @param url - Worker 入口脚本 URL
   * @returns 已就绪的 Worker
   */
  async getWorker(url: string): Promise<Worker> {
    if (!this.workers[url]) {
      this.workers[url] = []
      this.createdCount[url] = 0
      this.waitQueue[url] = []
      this.createChain[url] = Promise.resolve()
    }

    // 池中有空闲 Worker，直接返回
    if (this.workers[url].length > 0) {
      return this.workers[url].pop()!
    }

    // 未达上限，串行创建新 Worker
    if (this.createdCount[url] < this.maxWorkers) {
      this.createdCount[url]++

      // 链式串行：等前一个 Worker 创建完成后再创建，确保 wasm 已缓存
      const workerCreation = this.createChain[url].then(() => this._createAndWaitReady(url))
      this.createChain[url] = workerCreation.then(
        () => undefined,
        () => undefined
      )
      return workerCreation
    }

    // 已达上限，等待归还
    return new Promise<Worker>((resolve) => {
      this.waitQueue[url].push(resolve)
    })
  }

  /**
   * 归还 Worker 到池中，优先唤醒等待队列。
   *
   * @param url - Worker 入口脚本 URL
   * @param worker - 待归还的 Worker
   */
  returnWorker(url: string, worker: Worker) {
    const waiter = this.waitQueue[url]?.shift()
    if (waiter) {
      waiter(worker)
    } else if (this.workers[url]) {
      this.workers[url].push(worker)
    } else {
      worker.terminate()
    }
  }

  /**
   * 丢弃一个不可复用的 Worker。
   *
   * @param url - Worker 入口脚本 URL
   * @param worker - 待终止的 Worker
   */
  discardWorker(url: string, worker: Worker): void {
    worker.terminate()
    if (this.createdCount[url] > 0) {
      this.createdCount[url]--
    }

    const waiter = this.waitQueue[url]?.shift()
    if (waiter) {
      void this.getWorker(url).then(waiter)
    }
  }

  /** 终止并释放所有 worker */
  dispose(): void {
    for (const url in this.workers) {
      for (const worker of this.workers[url]) {
        worker.terminate()
      }
    }
    this.workers = {}
    this.createdCount = {}
    this.waitQueue = {}
    this.createChain = {}
  }

  /** 创建 Worker 并等待其 ready 信号，加载失败时归还名额并 reject */
  private _createAndWaitReady(url: string): Promise<Worker> {
    return new Promise<Worker>((resolve, reject) => {
      const worker = new Worker(url, { type: 'module' })
      const cleanup = () => {
        worker.removeEventListener('message', onMessage)
        worker.removeEventListener('error', onError)
      }
      const onMessage = (e: MessageEvent) => {
        if (e.data?.type === 'ready') {
          cleanup()
          resolve(worker)
        }
      }
      const onError = (e: ErrorEvent) => {
        cleanup()
        worker.terminate()
        this.createdCount[url]--
        const reason =
          e.message ||
          (e.error instanceof Error ? e.error.message : '') ||
          e.filename ||
          `error event (${e.type})`
        reject(new Error(`Worker 创建失败 (${url}): ${reason}`))
      }
      worker.addEventListener('message', onMessage)
      worker.addEventListener('error', onError)
    })
  }
}
